/**
 * Password generator. Browser-only (uses `crypto.getRandomValues`).
 *
 * Why this is a P0 lib:
 * - `Math.random()` is forbidden — it's a PRNG, not a CSPRNG.
 * - The naive `randomInt(0, charset.length)` via modulo is biased
 *   when `charset.length` doesn't divide 2³², which means low-entropy
 *   characters (e.g. '0' / '1' in a 62-char set) repeat slightly
 *   more often. We do rejection sampling so the distribution is
 *   strictly uniform.
 *
 * Strength estimate is the simple Shannon-style upper bound
 *   bits = length * log2(|charset|)
 * It is *not* zxcvbn (which we punt to P1). It's a hard upper bound,
 * not a realistic estimate (cracking dictionaries beat it for human
 * patterns), so we present it honestly as "粗估强度".
 */

const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!@#$%^&*()-_=+[]{}<>,.?/|;:`~"\'\\';

/** Visually-ambiguous: 0/O/o, I/l/1. */
const AMBIGUOUS = new Set('0OoIl1');
/** Hard-to-type on mobile / shell-unfriendly. */
const NON_TYPEABLE = new Set('{}[]()/\'"`~,;:.<>\\|');

export interface PasswordOpts {
  /** [4, 128]; values outside this range are clamped. */
  length: number;
  charsets: {
    lower: boolean;
    upper: boolean;
    digits: boolean;
    symbols: boolean;
  };
  /** Strip 0OoIl1. */
  excludeAmbiguous?: boolean;
  /** Strip {}[]()/'"`~,;:.<>\|. */
  excludeNonTypeable?: boolean;
  /** [1, 100]; values outside this range are clamped. */
  count: number;
}

export function buildCharset(opts: PasswordOpts): string {
  let pool = '';
  if (opts.charsets.lower) pool += LOWER;
  if (opts.charsets.upper) pool += UPPER;
  if (opts.charsets.digits) pool += DIGITS;
  if (opts.charsets.symbols) pool += SYMBOLS;
  if (opts.excludeAmbiguous) {
    pool = Array.from(pool)
      .filter((c) => !AMBIGUOUS.has(c))
      .join('');
  }
  if (opts.excludeNonTypeable) {
    pool = Array.from(pool)
      .filter((c) => !NON_TYPEABLE.has(c))
      .join('');
  }
  // Defensive de-dupe — the above filters can never produce dupes
  // today, but symbol overlaps in custom charsets later might.
  return Array.from(new Set(pool)).join('');
}

export function generatePasswords(opts: PasswordOpts): string[] {
  const length = clamp(opts.length, 4, 128);
  const count = clamp(opts.count, 1, 100);
  const charset = buildCharset(opts);
  if (charset.length === 0) {
    throw new Error('charset: 所有字符集都被排除，请至少启用一组');
  }
  return Array.from({ length: count }, () => generateOne(length, charset));
}

function generateOne(length: number, charset: string): string {
  // Rejection sampling: read 32-bit words, discard those above the
  // largest multiple of charset.length that fits in 2³². This makes
  // every character equally likely.
  const max = Math.floor(0xffffffff / charset.length) * charset.length;
  const out: string[] = new Array(length);
  let filled = 0;
  // Pull random words in batches; on the rare rejection we just grow
  // the buffer rather than re-allocating per character.
  const batchSize = Math.max(length * 2, 32);
  while (filled < length) {
    const buf = new Uint32Array(batchSize);
    crypto.getRandomValues(buf);
    for (let i = 0; i < buf.length && filled < length; i++) {
      const v = buf[i];
      if (v >= max) continue;
      out[filled++] = charset[v % charset.length];
    }
  }
  return out.join('');
}

export function estimateEntropyBits(length: number, charsetSize: number): number {
  if (length <= 0 || charsetSize <= 1) return 0;
  return length * Math.log2(charsetSize);
}

export type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong' | 'excellent';

/**
 * Bands picked to align with NIST SP 800-63B / OWASP guidance:
 *  < 28  weak       — trivially crackable offline
 *  < 36  fair       — survives online throttled attacks
 *  < 60  good       — survives modest offline attack
 *  < 128 strong     — comfortably above modern offline brute-force
 *  >=128 excellent  — basically future-proof for any non-quantum threat
 */
export function classifyStrength(bits: number): PasswordStrength {
  if (bits < 28) return 'weak';
  if (bits < 36) return 'fair';
  if (bits < 60) return 'good';
  if (bits < 128) return 'strong';
  return 'excellent';
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  const x = Math.floor(n);
  if (x < lo) return lo;
  if (x > hi) return hi;
  return x;
}
